import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];
type Job = Database["public"]["Tables"]["jobs"]["Row"];

// Roles that should use the limited view (no door_code, garage_code)
const RESTRICTED_ROLES: AppRole[] = ["Viewer", "Procurement"];

// Type for jobs from the limited view (excludes sensitive fields)
export type JobLimited = Omit<Job, "door_code" | "garage_code">;

// Union type for components that handle both
export type JobData = Job | JobLimited;

/**
 * Determines which jobs table/view to query based on user roles.
 * - Restricted roles (Viewer, Procurement) use jobs_limited view
 * - All other roles use the full jobs table
 */
export function getJobsTableName(roles: AppRole[]): "jobs" | "jobs_limited" {
  // If user has any non-restricted role, they get full access
  const hasFullAccess = roles.some(role => !RESTRICTED_ROLES.includes(role));
  return hasFullAccess ? "jobs" : "jobs_limited";
}

/**
 * Check if user has access to sensitive job fields (door_code, garage_code)
 */
export function canAccessSensitiveFields(roles: AppRole[]): boolean {
  return roles.some(role => !RESTRICTED_ROLES.includes(role));
}

/**
 * Helper to check if a job object has sensitive fields
 */
export function hasSensitiveFields(job: JobData): job is Job {
  return "door_code" in job;
}

/**
 * Fetch user roles from the database
 */
export async function fetchUserRoles(): Promise<AppRole[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data: roles } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", user.id);

  return roles?.map(r => r.role as AppRole) || [];
}

/**
 * Fetches jobs list using role-based view selection
 */
export async function fetchJobsList(roles: AppRole[], orgId?: string | null): Promise<{ data: JobData[] | null; error: Error | null }> {
  const hasFullAccess = canAccessSensitiveFields(roles);
  
  if (hasFullAccess) {
    let query = supabase.from("jobs").select("*").order("created_at", { ascending: false });
    if (orgId) query = query.eq("org_id", orgId);
    const { data, error } = await query;
    return { data, error: error as Error | null };
  } else {
    let query = supabase.from("jobs_limited").select("*").order("created_at", { ascending: false });
    // jobs_limited view inherits org_id filter from RLS
    const { data, error } = await query;
    return { data: data as JobData[] | null, error: error as Error | null };
  }
}

/**
 * Fetches a single job by ID using role-based view selection
 */
export async function fetchJobById(id: string, roles: AppRole[]): Promise<{ data: JobData | null; error: Error | null }> {
  const hasFullAccess = canAccessSensitiveFields(roles);
  
  if (hasFullAccess) {
    const { data, error } = await supabase
      .from("jobs")
      .select("*")
      .eq("id", id)
      .single();
    return { data, error: error as Error | null };
  } else {
    const { data, error } = await supabase
      .from("jobs_limited")
      .select("*")
      .eq("id", id)
      .single();
    return { data: data as JobData | null, error: error as Error | null };
  }
}

/**
 * Fetches jobs for dropdowns (id, name, job_number only)
 */
export async function fetchJobsForDropdown(roles: AppRole[]): Promise<{ data: Array<{ id: string; name: string; job_number: string | null }> | null; error: Error | null }> {
  const hasFullAccess = canAccessSensitiveFields(roles);
  const tableName = hasFullAccess ? "jobs" : "jobs_limited";
  
  if (hasFullAccess) {
    const { data, error } = await supabase
      .from("jobs")
      .select("id, name, job_number")
      .order("name", { ascending: true });
    return { data, error: error as Error | null };
  } else {
    const { data, error } = await supabase
      .from("jobs_limited")
      .select("id, name, job_number")
      .order("name", { ascending: true });
    return { data, error: error as Error | null };
  }
}
